import {atom} from 'nanostores'

export interface ExperimentState {
    assignments: Record<string, string>
    encountered: Record<string, boolean>
    paths: string[]
}

const EMPTY_STATE: ExperimentState = {assignments: {}, encountered: {}, paths: []}

export const $experiments = atom<ExperimentState>(EMPTY_STATE)
export const $experimentsReady = atom<boolean>(false)

export const isExperimentsReady = (): boolean => $experimentsReady.get()
export const getExperimentAssignments = (): Record<string, string> => $experiments.get().assignments
export const getEncounteredExperiments = (): Record<string, boolean> =>
    $experiments.get().encountered

const DEFAULT_TIMEOUT_MS = 3000

// Note: the response also carries `assignmentId`; the client does not consume it (server-side debug aid only).
interface ExperimentsApiResponse {
    experiments?: {
        'pleo-split-test'?: Record<string, string>
        'pleo-encountered-split-tests'?: Record<string, boolean>
        'pleo-split-test-paths'?: string[]
    }
}

let loadPromise: Promise<void> | null = null

/**
 * Fetches the visitor's experiment assignment once and stores it. Always resolves and
 * flips $experimentsReady — on success, failure, or timeout — so it can never block the
 * consent gate / Segment load. Subsequent calls return the same in-flight/completed promise.
 */
export const loadExperiments = (
    path: string,
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<void> => {
    // Single-flight and one-shot: a failed/empty load is cached intentionally (no retry) — the empty-but-ready store is the safe default for the consent gate.
    if (loadPromise) {
        return loadPromise
    }

    loadPromise = (async () => {
        const controller = new AbortController()
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        const timeout = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => {
                controller.abort()
                reject(new Error('experiments fetch timed out'))
            }, timeoutMs)
        })

        try {
            // Suppress the losing arm's rejection: when the timeout wins the race, the
            // aborted fetch rejects afterwards with no attached handler, which would
            // surface as an unhandled promise rejection. Catching it here (resolving to
            // undefined) keeps that quiet; the empty-store outcome is handled below.
            const fetchResult = fetch(`/api/experiments?path=${encodeURIComponent(path)}`, {
                signal: controller.signal,
            }).catch(() => undefined)

            const response = await Promise.race([fetchResult, timeout])

            if (response?.ok) {
                const json: ExperimentsApiResponse = await response.json()
                const experiments = json.experiments ?? {}
                $experiments.set({
                    assignments: experiments['pleo-split-test'] ?? {},
                    encountered: experiments['pleo-encountered-split-tests'] ?? {},
                    paths: experiments['pleo-split-test-paths'] ?? [],
                })
            }
        } catch {
            // Timeout or a malformed JSON body: leave $experiments empty. Silent graceful
            // degradation is intentional — experiment traits are best-effort enrichment.
        } finally {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId)
            }
            $experimentsReady.set(true)
        }
    })()

    return loadPromise
}
